export interface FaqProvincia {
  domanda: string;
  risposta: string;
}

export interface CasoReale {
  titolo: string;
  luogo: string;
  problema: string;
  intervento: string;
  risultato: string;
}

export interface ServizioLocale {
  titolo: string;
  testo: string;
  /** Link interno contestuale al servizio descritto */
  link?: { to: string; label: string };
}


export interface PaginaProvincia {
  slug: string;
  citta: string;
  sigla: string;
  geo: string;
  /** Coordinate del capoluogo, per i dati strutturati LocalBusiness */
  lat: number;
  lon: number;
  /** Raggio di servizio in metri attorno al capoluogo */
  raggio: number;
  /** Title SEO personalizzato (max ~60 caratteri) */
  titoloSeo?: string;
  /** Meta description SEO personalizzata (max ~155 caratteri) */
  descrizioneSeo?: string;
  intro: string;
  zone: string[];
  /** Elenco esteso di comuni serviti, per la copertura delle query locali */
  comuni?: string[];
  /** Servizi descritti con linguaggio e contesto locale */
  serviziLocali?: ServizioLocale[];
  /** Casi reali di lavori svolti in provincia */
  casi?: CasoReale[];
  /** Link interni utili per aumentare CTR e conversioni */
  linkUtili?: { to: string; label: string; testo: string }[];
  contesto: string;

  distanza: string;
  faq: FaqProvincia[];
}


const faqComuni = (citta: string): FaqProvincia[] => [
  {
    domanda: `Quanto costa il lavaggio e trattamento di un pavimento in cotto a ${citta}?`,
    risposta: `Il prezzo dipende dai metri quadri, dallo stato del cotto e dal tipo di trattamento finale (cera o protettivo traspirante). Facciamo un sopralluogo gratuito a ${citta} e provincia e consegniamo un preventivo scritto, dettagliato e senza impegno.`,
  },
  {
    domanda: "Fate anche la levigatura o la lucidatura a piombo del marmo?",
    risposta:
      "No. Su marmi, pietre naturali e graniglie eseguiamo solo lavaggio e trattamento protettivo antimacchia di pavimenti, scale e davanzali. Non facciamo levigatura, lucidatura a piombo né cristallizzazione.",
  },
  {
    domanda: "Come togliete le cere vecchie e ingiallite dal cotto?",
    risposta:
      "Con una deceratura mirata: sciogliamo gli strati di cera e i residui di detersivo, risciacquiamo a fondo e solo dopo, a pavimento asciutto, applichiamo la nuova cera o il protettivo traspirante.",
  },
  {
    domanda: `Si può abitare in casa durante i lavori a ${citta}?`,
    risposta:
      "Sì, nella maggior parte dei casi. Lavoriamo per zone e in orari concordati, proteggiamo battiscopa e arredi e lasciamo il cantiere pulito. Le stanze trattate restano libere solo per il tempo di asciugatura.",
  },
];

export const paginaProvincia: Record<string, PaginaProvincia> = {

  brescia: {
    slug: "trattamento-pavimenti-cotto-brescia",
    citta: "Brescia",
    sigla: "BS",
    geo: "IT-25",
    lat: 45.5416,
    lon: 10.2118,
    raggio: 45000,
    titoloSeo: "Pavimenti in cotto Brescia: lavaggio e trattamento",
    descrizioneSeo:
      "Lavaggio, deceratura e trattamento del cotto a Brescia e provincia. Artigiani dal 1990, sopralluogo e preventivo gratuiti: chiama 339 751 7813.",
    intro:
      "Siamo di Torbole Casaglia, a pochi chilometri da Brescia: è la provincia in cui lavoriamo da più tempo e dove arriviamo anche per un solo sopralluogo, senza costi aggiuntivi.",
    zone: [
      "Brescia città",
      "Franciacorta",
      "Lago di Garda (Desenzano, Salò, Sirmione)",
      "Val Trompia e Val Sabbia",
      "Bassa bresciana (Manerbio, Leno, Ghedi)",
      "Chiari, Rovato, Palazzolo",
    ],
    comuni: [
      "Brescia", "Torbole Casaglia", "Desenzano del Garda", "Salò", "Sirmione", "Gardone Riviera",
      "Rovato", "Chiari", "Palazzolo sull'Oglio", "Iseo", "Erbusco", "Adro", "Gussago",
      "Concesio", "Gardone Val Trompia", "Lumezzane", "Manerbio", "Leno", "Ghedi", "Montichiari",
      "Orzinuovi", "Travagliato", "Rezzato", "Botticino",
    ],
    serviziLocali: [
      {
        titolo: "Cotto delle cascine e delle ville di Franciacorta",
        testo:
          "Pavimenti in cotto steso a mano, spesso enormi e con cotture diverse tra una stanza e l'altra. Lavaggio calibrato zona per zona e finitura a cera o protettivo opaco, a seconda dell'uso.",
        link: { to: "/come-pulire-il-cotto", label: "Come pulire il cotto: guida pratica" },
      },
      {
        titolo: "Porticati e terrazze sul Lago di Garda",
        testo:
          "Tra Desenzano, Sirmione e Salò il cotto esterno soffre umidità, muschi e gelo. Lavaggio profondo, rimozione di alghe e sali, poi protettivo traspirante che regge pioggia e sbalzi termici.",
        link: { to: "/trattamento-cotto-esterno", label: "Trattamento cotto esterno" },
      },
      {
        titolo: "Marmi, pietre e graniglie in città",
        testo:
          "A Brescia città, Rezzato e Botticino trattiamo pavimenti, scale e davanzali in marmo e graniglia con lavaggio e protettivo antimacchia. Non facciamo levigatura né lucidatura a piombo.",
        link: { to: "/pulizia-lavaggio-cotto", label: "Pulizia e lavaggio del cotto" },
      },
      {
        titolo: "Grès e cemento della bassa bresciana",
        testo:
          "Nelle case nuove tra Manerbio, Leno, Ghedi e Montichiari togliamo velo di posa, aloni e sporco incrostato dal grès, con protezione antimacchia dove serve.",
        link: { to: "/pulizia-lavaggio-cotto", label: "Servizio di pulizia pavimenti" },
      },
    ],
    linkUtili: [
      {
        to: "/trattamento-cotto-esterno",
        label: "Trattamento cotto esterno",
        testo: "Hai un cortile, un porticato o una terrazza? Scopri il trattamento specifico per il cotto esterno.",
      },
      {
        to: "/pulizia-lavaggio-cotto",
        label: "Pulizia e lavaggio del cotto",
        testo: "Servizio completo di pulizia, deceratura e trattamento protettivo per pavimenti in cotto.",
      },
      {
        to: "/come-pulire-il-cotto",
        label: "Come pulire il cotto",
        testo: "Guida pratica con i consigli dei nostri artigiani per mantenere il cotto senza rovinarlo.",
      },
    ],

    casi: [
      {
        titolo: "Cortile in cotto di una cascina ristrutturata",
        luogo: "Franciacorta (BS)",
        problema:
          "Oltre 120 m² di cotto esterno con muschio nelle fughe, patina verde negli angoli in ombra e sali affiorati dopo l'inverno.",
        intervento:
          "Lavaggio meccanico a bassa pressione con detergente dedicato, rimozione di muschi e alghe, deacidificazione delle zone con efflorescenze e protettivo traspirante antigelo.",
        risultato:
          "Cortile riportato al colore originale, con superficie che asciuga in fretta e non trattiene più lo sporco.",
      },
      {
        titolo: "Terrazza sul lago con cotto opacizzato",
        luogo: "Desenzano del Garda (BS)",
        problema:
          "Terrazza vista lago con cotto grigiastro, aloni di calcare dell'irrigazione e vecchio protettivo sfogliato a chiazze.",
        intervento:
          "Rimozione del residuo di trattamento, lavaggio anticalcare, risciacquo abbondante e doppia mano di protettivo idro-oleorepellente a effetto naturale.",
        risultato:
          "Colore uniforme senza effetto plastica e acqua che perla, con manutenzione ridotta a un lavaggio neutro stagionale.",
      },
      {
        titolo: "Pavimento interno in cotto con cere ingiallite",
        luogo: "Brescia città",
        problema:
          "Appartamento di 90 m² con cotto scurito dalle cere accumulate negli anni e aloni evidenti davanti alle porte finestre.",
        intervento:
          "Deceratura completa, lavaggio a fondo delle fughe, asciugatura e nuova cera in pasta lucidata, lavorando per stanze così da lasciare la casa abitabile.",
        risultato:
          "Tonalità calda recuperata in modo uniforme e famiglia rimasta in casa per tutta la durata del lavoro.",
      },
    ],

    contesto:
      "Cascine ristrutturate, ville di Franciacorta, porticati e terrazze sul Garda: nel Bresciano il cotto è ovunque, spesso antico e steso a mano. Su questi pavimenti non si passa una macchina e via: si lavora zona per zona, calibrando il lavaggio sulla cottura del laterizio.",
    distanza: "Sopralluogo in giornata in tutta la provincia.",
    faq: [
      {
        domanda: "Trattate il cotto esterno di porticati e terrazze sul Garda?",
        risposta:
          "Sì. Su cortili, porticati e terrazze del Bresciano e della zona del Garda facciamo lavaggio profondo, rimozione di muschi e sali e trattamento protettivo traspirante, studiato per resistere a pioggia e gelo.",
      },
      ...faqComuni("Brescia"),
    ],
  },

  bergamo: {
    slug: "trattamento-pavimenti-cotto-bergamo",
    citta: "Bergamo",
    sigla: "BG",
    geo: "IT-25",
    lat: 45.6983,
    lon: 9.6773,
    raggio: 40000,
    titoloSeo: "Trattamento superfici a Bergamo: cotto, marmo, pietra e graniglia",
    descrizioneSeo:
      "Trattamento protettivo e antimacchia per superfici in cotto, marmo e pietra a Bergamo e provincia. Sopralluogo e preventivo gratuiti: chiama 339 751 7813.",
    intro:
      "Bergamo e provincia sono a meno di un'ora dalla nostra sede: interveniamo su cotto, marmo, graniglia e grès sia in città che nelle valli.",
    zone: [
      "Bergamo città e Città Alta",
      "Valle Seriana",
      "Valle Brembana",
      "Val Cavallina e Lago d'Iseo",
      "Treviglio e la bassa bergamasca",
      "Isola Bergamasca",
    ],
    comuni: [
      "Bergamo", "Città Alta", "Treviglio", "Dalmine", "Seriate", "Albino", "Alzano Lombardo",
      "Nembro", "Clusone", "Romano di Lombardia", "Caravaggio", "Zogno", "San Pellegrino Terme",
      "Sarnico", "Trescore Balneario", "Ponte San Pietro", "Osio Sotto", "Stezzano", "Curno",
      "Almenno San Salvatore", "Lovere", "Grumello del Monte",
    ],
    serviziLocali: [
      {
        titolo: "Cotto antico di Città Alta e dei borghi",
        testo:
          "Pavimenti in cotto fatto a mano, spesso sotto strati di cera ingiallita. Deceratura delicata, lavaggio a bassa aggressività e cera o protettivo traspirante, senza mai asportare la pelle del laterizio.",
        link: { to: "/come-pulire-il-cotto", label: "Come pulire il cotto: guida pratica" },
      },
      {
        titolo: "Salnitro e umidità nelle case di valle",
        testo:
          "Nelle case della Val Seriana, Brembana e Cavallina l'umidità di risalita porta a fiorire i sali. Lavaggio deacidificante, rimozione delle efflorescenze e protettivo che lascia respirare il pavimento.",
        link: { to: "/pulizia-lavaggio-cotto", label: "Pulizia e lavaggio del cotto" },
      },
      {
        titolo: "Graniglie, marmi e scale condominiali",
        testo:
          "Nelle palazzine di Bergamo, Dalmine, Seriate e Treviglio laviamo e proteggiamo graniglie alla veneziana, marmi di scale e ingressi. Solo lavaggio e protettivo: niente levigatura o lucidatura a piombo.",
        link: { to: "/pulizia-lavaggio-cotto", label: "Servizio di pulizia pavimenti" },
      },
      {
        titolo: "Terrazze e porticati della bassa bergamasca",
        testo:
          "Cotto esterno di villette e cascine tra Treviglio, Caravaggio e Romano di Lombardia: rimozione di muschi, alghe e patina grigia, poi protettivo antigelo traspirante.",
        link: { to: "/trattamento-cotto-esterno", label: "Trattamento cotto esterno" },
      },
    ],
    linkUtili: [
      {
        to: "/trattamento-cotto-esterno",
        label: "Trattamento cotto esterno",
        testo: "Hai un cortile, un porticato o una terrazza a Bergamo? Scopri il trattamento specifico per il cotto esterno.",
      },
      {
        to: "/pulizia-lavaggio-cotto",
        label: "Pulizia e lavaggio del cotto",
        testo: "Servizio completo di pulizia, deceratura e trattamento protettivo per pavimenti in cotto.",
      },
      {
        to: "/come-pulire-il-cotto",
        label: "Come pulire il cotto",
        testo: "Guida pratica con i consigli dei nostri artigiani per mantenere il cotto senza rovinarlo.",
      },
    ],

    casi: [
      {
        titolo: "Cotto antico in appartamento in Città Alta",
        luogo: "Bergamo Alta (BG)",
        problema:
          "Circa 70 m² di cotto fatto a mano con quattro o cinque strati di cera vecchia, aloni scuri lungo i passaggi e fughe annerite.",
        intervento:
          "Deceratura in due passate, lavaggio manuale zona per zona rispettando il pavimento d'epoca, pulizia delle fughe e finitura a cera in pasta lucidata a macchina lenta.",
        risultato:
          "Colore rosso caldo tornato uniforme, superficie protetta e manutenibile con normale lavaggio neutro.",
      },
      {
        titolo: "Sali e macchie bianche in una casa di valle",
        luogo: "Valle Seriana (BG)",
        problema:
          "Velo bianco di salnitro diffuso su un piano terra in cotto, ricomparso dopo un precedente trattamento filmante.",
        intervento:
          "Rimozione del vecchio film, lavaggio deacidificante ripetuto, asciugatura controllata e applicazione di un protettivo traspirante idro-oleorepellente.",
        risultato:
          "Efflorescenze eliminate e pavimento che continua a traspirare, senza il ritorno immediato dell'alone bianco.",
      },
      {
        titolo: "Scala e ingresso in graniglia di una palazzina",
        luogo: "Seriate (BG)",
        problema:
          "Scala condominiale in graniglia opacizzata da residui di detersivo, con gradini scivolosi quando bagnati.",
        intervento:
          "Lavaggio professionale a fondo dei gradini e del pianerottolo, rimozione dei residui e applicazione di protettivo antimacchia, lavorando per mezze rampe.",
        risultato:
          "Ingresso più chiaro e uniforme, con passaggio sempre garantito ai condomini durante i lavori.",
      },
    ],

    contesto:
      "In Città Alta e nelle valli troviamo molto cotto antico e pavimenti alla veneziana, superfici delicate che vanno pulite senza aggredire il fondo. Nelle case di valle il problema più frequente è invece l'umidità di risalita, che porta a fiorire i sali sulla superficie.",
    distanza: "Sopralluogo gratuito, di solito entro pochi giorni dalla richiesta.",
    faq: [
      {
        domanda: "Si possono togliere le macchie bianche di salnitro dal cotto?",
        risposta:
          "Nella maggior parte dei casi sì: con un lavaggio deacidificante rimuoviamo i sali affiorati e poi applichiamo un protettivo traspirante, che lascia respirare il laterizio invece di sigillarlo. Se l'umidità di risalita è forte lo diciamo subito in fase di sopralluogo.",
      },
      ...faqComuni("Bergamo"),
    ],
  },

  milano: {
    slug: "trattamento-pavimenti-cotto-milano",
    citta: "Milano",
    sigla: "MI",
    geo: "IT-25",
    lat: 45.4642,
    lon: 9.19,
    raggio: 40000,
    titoloSeo: "Trattamento pavimenti in cotto a Milano e hinterland",
    descrizioneSeo:
      "Lavaggio, deceratura e trattamento di cotto, marmo e graniglia a Milano e hinterland. Sopralluogo e preventivo gratuiti: chiama 339 751 7813.",
    intro:
      "A Milano e in tutta l'area metropolitana lavoriamo soprattutto su cotto d'appartamento, terrazzi, marmo di scale e ingressi condominiali.",
    zone: [
      "Milano città",
      "Hinterland nord (Sesto, Cinisello, Monza)",
      "Hinterland ovest (Rho, Corsico, Magenta)",
      "Hinterland sud (Rozzano, San Donato, Melegnano)",
      "Martesana e Adda (Gorgonzola, Cassano)",
      "Legnano, Abbiategrasso, Saronno",
    ],
    comuni: [
      "Milano", "Sesto San Giovanni", "Cinisello Balsamo", "Monza", "Rho", "Corsico", "Magenta",
      "Rozzano", "San Donato Milanese", "San Giuliano Milanese", "Melegnano", "Gorgonzola",
      "Cassano d'Adda", "Legnano", "Abbiategrasso", "Saronno", "Segrate", "Pioltello",
      "Bollate", "Paderno Dugnano", "Cologno Monzese", "Buccinasco", "Cesano Boscone", "Melzo",
    ],
    serviziLocali: [
      {
        titolo: "Cotto d'appartamento in città",
        testo:
          "Nei palazzi storici di Milano il cotto è quasi sempre coperto da cere accumulate negli anni. Deceratura completa, lavaggio a fondo e nuova cera o protettivo opaco, lavorando per stanze così da lasciare la casa vivibile.",
        link: { to: "/pulizia-lavaggio-cotto", label: "Pulizia e lavaggio del cotto" },
      },
      {
        titolo: "Terrazzi e balconi con patina grigia",
        testo:
          "Smog e piogge acide lasciano sul cotto esterno una patina grigia che il lavaggio domestico non toglie. Lavaggio professionale, rimozione di alghe e sali, poi protettivo traspirante antigelo.",
        link: { to: "/trattamento-cotto-esterno", label: "Trattamento cotto esterno" },
      },
      {
        titolo: "Scale, ingressi e androni condominiali",
        testo:
          "Marmo, graniglia alla veneziana e ceppo di ingressi e pianerottoli: lavaggio a fondo e protettivo antimacchia, in orari concordati con l'amministratore. Non facciamo levigatura né lucidatura a piombo.",
        link: { to: "/trattamento-marmo-pietre-graniglia", label: "Marmo, pietre e graniglia" },
      },
      {
        titolo: "Grès di uffici, negozi e case nuove",
        testo:
          "Velo di posa, aloni di detersivo e fughe annerite sul grès porcellanato di appartamenti nuovi, uffici e locali dell'hinterland, con trattamento antimacchia dove serve.",
        link: { to: "/pulizia-trattamento-gres", label: "Pulizia e trattamento del grès" },
      },
    ],
    linkUtili: [
      {
        to: "/trattamento-cotto-esterno",
        label: "Trattamento cotto esterno",
        testo: "Hai un terrazzo o un balcone in cotto a Milano? Scopri il trattamento specifico per gli esterni.",
      },
      {
        to: "/pulizia-lavaggio-cotto",
        label: "Pulizia e lavaggio del cotto",
        testo: "Servizio completo di pulizia, deceratura e trattamento protettivo per pavimenti in cotto.",
      },
      {
        to: "/come-pulire-il-cotto",
        label: "Come pulire il cotto",
        testo: "Guida pratica con i consigli dei nostri artigiani per mantenere il cotto senza rovinarlo.",
      },
    ],
    casi: [
      {
        titolo: "Cotto di un appartamento in palazzo d'epoca",
        luogo: "Milano centro",
        problema:
          "110 m² di cotto scurito da cere sovrapposte, con aloni marcati lungo il corridoio e nelle zone di passaggio.",
        intervento:
          "Deceratura in più passate, lavaggio delle fughe, asciugatura controllata e finitura a cera in pasta lucidata, procedendo stanza per stanza.",
        risultato:
          "Colore caldo e uniforme recuperato, con la famiglia rimasta in casa per tutta la durata del lavoro.",
      },
      {
        titolo: "Terrazzo in cotto con patina da smog",
        luogo: "Sesto San Giovanni (MI)",
        problema:
          "Terrazzo di 40 m² con superficie grigia, depositi neri negli angoli e vecchio protettivo sfogliato.",
        intervento:
          "Rimozione del residuo di trattamento, lavaggio profondo a bassa pressione e doppia mano di protettivo traspirante idro-oleorepellente.",
        risultato:
          "Cotto tornato rosso, acqua che perla e manutenzione ridotta a un lavaggio neutro stagionale.",
      },
      {
        titolo: "Androne e scala in graniglia di un condominio",
        luogo: "Cinisello Balsamo (MI)",
        problema:
          "Ingresso opacizzato da residui di detersivo e gradini scivolosi quando bagnati.",
        intervento:
          "Lavaggio professionale di androne, gradini e pianerottoli e applicazione di protettivo antimacchia, lavorando per mezze rampe.",
        risultato:
          "Ingresso più chiaro e uniforme, con passaggio sempre garantito ai condomini.",
      },
    ],
    contesto:
      "In città il cotto è quasi sempre in interno: cere vecchie ingiallite, aloni di detersivo e traffico intenso. Nei terrazzi, invece, smog e piogge acide lasciano una patina grigia che solo un lavaggio professionale toglie davvero. Lavoriamo in orari concordati e lasciamo il cantiere pulito.",
    distanza: "Interveniamo in giornata concordata, anche in appartamento abitato.",

    faq: [
      {
        domanda: "Lavorate anche in condominio, su scale e ingressi?",
        risposta:
          "Sì. A Milano e nell'hinterland puliamo e trattiamo scale, pianerottoli e ingressi condominiali in marmo, graniglia o cotto, concordando orari e fasi con l'amministratore per lasciare sempre un passaggio libero.",
      },
      ...faqComuni("Milano"),
    ],
  },

};

export const slugProvincia = Object.values(paginaProvincia).map((p) => p.slug);
